<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useDeployStore } from "@/stores/deploy";
import { usePendingChangesStore } from "@/stores/pendingChanges";
import { useToastStore } from "@/stores/toast";

// Page-wide「尚未發佈」bar at the top of the portal content area. Shows how many
// saved changes the static public site doesn't have yet (pages, events, media,
// documents, media groups, footer — anything that needs a rebuild), with a
// 發佈更新 button and live deploy status. On fullscreen builder routes the
// floating DeployBanner pill covers the status instead (see App.vue).
const auth = useAuthStore();
const deploy = useDeployStore();
const pending = usePendingChangesStore();
const toast = useToastStore();

const TYPE_LABELS = {
  page: "頁面",
  event: "活動",
  media: "媒體報導",
  document: "文件",
  media_group: "媒體分組",
  footer: "頁尾",
};

const showDetails = ref(false);

// Both endpoints need a JWT — wait for login. deploy.init() also resumes
// tracking a deploy already running (page refresh / a colleague's publish).
watch(() => auth.user, (u) => {
  if (u) { deploy.init(); pending.refresh(); }
}, { immediate: true });

// Saves in THIS tab refresh instantly via onAdminMutation; this slow poll only
// picks up a colleague's edits from another machine.
const REFRESH_MS = 60_000;
let pollTimer = null;
onMounted(() => { pollTimer = setInterval(() => pending.refresh(), REFRESH_MS); });

// A terminal deploy state means the baseline moved server-side — re-check the
// count, and auto-dismiss the success message (failures stay until dismissed).
// "untracked" means the rebuild was dispatched but progress can't be followed
// (e.g. the GitHub token can't read workflow runs): keep showing "deploying"
// for the realistic build window instead of a premature ✓, then assume done.
const AUTO_HIDE_MS = 6000;
const UNTRACKED_ASSUME_DONE_MS = 4 * 60 * 1000;
let hideTimer = null;
watch(() => deploy.state, (s) => {
  clearTimeout(hideTimer);
  if (s === "done" || s === "untracked" || s === "failed") pending.refresh();
  if (s === "done") hideTimer = setTimeout(() => deploy.dismiss(), AUTO_HIDE_MS);
  if (s === "untracked") hideTimer = setTimeout(() => deploy.dismiss(), UNTRACKED_ASSUME_DONE_MS);
});

onBeforeUnmount(() => { clearInterval(pollTimer); clearTimeout(hideTimer); });

const deployActive = computed(() =>
  ["pending", "deploying", "done", "untracked", "failed"].includes(deploy.state));
const visible = computed(() => deployActive.value || pending.count > 0);

const barClass = computed(() => {
  if (deploy.state === "failed") return "border-rose-200 bg-rose-50";
  if (deploy.state === "done") return "border-emerald-200 bg-emerald-50";
  if (deployActive.value) return "border-slate-200 bg-white";
  return "border-amber-200 bg-amber-50";
});

async function publishAll() {
  showDetails.value = false;
  const triggered = await deploy.trigger();
  if (triggered === null) { toast.error("無法觸發網站更新"); return; }
  if (!triggered) toast.info("本機環境沒有部署管線，公開網站不會更新");
  pending.refresh(); // the dispatch advanced the baseline server-side
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="opacity-0 -translate-y-1"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-1"
  >
    <div v-if="visible" class="shrink-0 px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4">
      <!-- ml-12 clears the floating mobile menu button -->
      <div class="ml-12 lg:ml-0 rounded-xl border shadow-sm" :class="barClass">
        <!-- Deploy in flight (untracked = dispatched but progress unfollowable:
             still show "deploying" for the build window, never a premature ✓) -->
        <div v-if="deploy.state === 'pending' || deploy.state === 'deploying' || deploy.state === 'untracked'" class="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700">
          <svg class="h-4 w-4 shrink-0 animate-spin text-brand-green" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span v-if="deploy.state === 'pending'">已觸發網站更新，等待部署開始…</span>
          <span v-else-if="deploy.state === 'deploying'">網站更新中…（約需 2–3 分鐘）</span>
          <span v-else>網站更新中…（約 3 分鐘後生效）</span>
          <button v-if="deploy.state === 'untracked'" type="button" class="ml-auto rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" @click="deploy.dismiss()">
            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Deploy finished -->
        <div v-else-if="deploy.state === 'done'" class="flex items-center gap-3 px-4 py-2.5 text-sm text-emerald-800">
          <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <span>網站已更新{{ deploy.finishedAt ? `（${deploy.finishedAt.toLocaleTimeString()}）` : "" }}</span>
          <button type="button" class="ml-auto rounded-md p-1 text-emerald-700/70 transition-colors hover:bg-emerald-100 hover:text-emerald-800" @click="deploy.dismiss()">
            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Deploy failed -->
        <div v-else-if="deploy.state === 'failed'" class="flex items-center gap-3 px-4 py-2.5 text-sm text-rose-700">
          <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <span>網站更新失敗</span>
          <a v-if="deploy.runUrl" :href="deploy.runUrl" target="_blank" rel="noopener" class="underline">查看詳情</a>
          <div class="ml-auto flex items-center gap-2">
            <button type="button" class="btn-secondary btn-sm" @click="publishAll">重試</button>
            <button type="button" class="rounded-md p-1 text-rose-500 transition-colors hover:bg-rose-100 hover:text-rose-700" @click="deploy.dismiss()">
              <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Pending changes -->
        <div v-else class="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5 text-sm text-amber-800">
          <svg class="h-4 w-4 shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
          </svg>
          <span>
            有 <strong class="font-semibold">{{ pending.count }}</strong> 項變更已儲存，尚未發佈到公開網站
          </span>
          <button
            type="button"
            class="font-medium text-amber-700 underline decoration-amber-400 underline-offset-2 transition-colors hover:text-amber-900"
            @click="showDetails = !showDetails"
          >{{ showDetails ? "收起" : "詳情" }}</button>
          <button type="button" class="btn-primary btn-sm ml-auto" :disabled="deploy.deploying" @click="publishAll">
            發佈更新
          </button>
        </div>

        <!-- Pending item list -->
        <ul v-if="showDetails && !deployActive" class="border-t border-amber-200/70 px-4 py-2">
          <li
            v-for="it in pending.items"
            :key="`${it.type}-${it.id ?? 'x'}`"
            class="flex items-center gap-2 py-1 text-sm text-amber-900"
          >
            <span class="badge bg-amber-100 text-amber-700">{{ TYPE_LABELS[it.type] || it.type }}</span>
            <span class="truncate">{{ it.label }}</span>
          </li>
        </ul>
      </div>
    </div>
  </Transition>
</template>
